import {
  Controllable,
  getControlsForNode,
  loadControlsForNode,
  useAppDispatch,
  useAppSelector,
  useOverridableFeatureFlags,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {useCallback, useEffect, useMemo} from 'react';
import {differenceInSeconds, parseISO} from 'date-fns';

const emptyControls: Controllable[] = [];

export const useControlLoader = (
  nodeId: string,
  autoRefreshStaleData: boolean,
  secondsThresholdForStaleData: number,
) => {
  const dispatch = useAppDispatch();
  const {Controls} = useOverridableFeatureFlags();

  const {status = 'NotLoaded', lastLoadedTimestamp, controls} = useAppSelector(getControlsForNode(nodeId)) ?? {};

  const refreshData = useCallback(() => {
    if (!Controls) return;
    if (status === 'Loading') return;

    dispatch(withAsyncThunkErrorHandling(() => loadControlsForNode(nodeId)));
  }, [Controls, dispatch, nodeId, status]);

  const refreshDataIfStale = useCallback(() => {
    const now = new Date();
    const lastLoadedDateTime = lastLoadedTimestamp ? parseISO(lastLoadedTimestamp) : undefined;

    if (!lastLoadedDateTime || differenceInSeconds(now, lastLoadedDateTime) >= secondsThresholdForStaleData) {
      refreshData();
    }
  }, [lastLoadedTimestamp, refreshData, secondsThresholdForStaleData]);

  useEffect(() => {
    if (!autoRefreshStaleData) return;

    refreshDataIfStale();
  }, [autoRefreshStaleData, refreshDataIfStale]);

  return useMemo(
    () => ({
      refreshData,
      refreshDataIfStale,
      status,
      controls: controls ?? emptyControls,
      lastLoadedTime: lastLoadedTimestamp,
    }),
    [controls, lastLoadedTimestamp, refreshData, refreshDataIfStale, status],
  );
};
